/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { getRouteQueryParam } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';

import {
  AbstractRestService,
  arrayOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';

import { Observable } from 'rxjs';

import { EmailTemplateType } from './email-template-type.enum';
import {
  EmailTemplateAutocompleteListItemDto,
  EmailTemplateDetailsDto,
  EmailTemplateListItemDto,
} from './email-template.dto';
import {
  EmailTemplateAutocompleteListMapper,
  EmailTemplateDetailsMapper,
  EmailTemplateListItemMapper,
  EmailTemplatesGridFilterMapper,
} from './email-template.mapper';
import { TemplatePlaceholderDto, TemplatePlaceholderFilterDto } from './template-placeholder.dto';
import { TemplatePlaceholderFilterMapper, TemplatePlaceholderMapper } from './template-placeholder.mapper';

export class EmailTemplatesGridFilter {
  constructor(public type: EmailTemplateType) {
  }
}

export type EmailTemplateDto = {
  canBeDeleted: boolean;
  description: string;
  etag: string;
  id: ID;
  name: string;
};

export type EmailPlaceholderAutocompleteQuery = {
  code?: string;
  emailTemplateTypeId: ID;
  id?: ID;
};

@Injectable()
export class EmailTemplateRestService extends AbstractRestService {

  static prepareForCreate(route: ActivatedRouteSnapshot): any {
    return {
      id: void 0,
      name: void 0,
      description: void 0,
      body: void 0,
      type: getRouteQueryParam(route, 'type'),
    };
  }

  constructor(settings: RestSettings) {
    super(settings);
  }

  /**
   * Grid data source for list of Email Templates
   * @param filter
   * @returns {SlicedDataSource<EmailTemplateListItemDto>}
   */
  findAllAsDataSource(filter: EmailTemplatesGridFilter): SlicedDataSource<EmailTemplateListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/templates`, filter, EmailTemplatesGridFilterMapper, arrayOf(EmailTemplateListItemMapper));
  }

  /**
   * Single Email Template Observable
   * @param id
   * @returns {Observable<EmailTemplateListItemDto>}
   */
  get(id: ID): Observable<EmailTemplateDetailsDto> {
    return this.httpGet(`${this.bp}/templates/${id}`, optimisticLockingOf(EmailTemplateDetailsMapper));
  }

  /**
   * Single Email Template Observable
   * @returns {Observable<EmailTemplateListItemDto>}
   */
  initEmailTemplate(): Observable<EmailTemplateDetailsDto> {
    return this.httpGet(`${this.bp}/templates/init`, optimisticLockingOf(EmailTemplateDetailsMapper));
  }

  /**
   * Create new Email Template Observable
   * @param {EmailTemplateListItemDto} emailTemplate
   * @param overwrite
   * @returns {Observable<EmailTemplateListItemDto>}
   */
  create(emailTemplate: EmailTemplateDetailsDto, overwrite: boolean = false): Observable<EmailTemplateDetailsDto> {
    return this.httpPost(
      `${this.bp}/templates?overwrite=${overwrite}`,
      emailTemplate,
      optimisticLockingOf(EmailTemplateDetailsMapper));
  }

  /**
   * Update single Email Template Observable
   * @param {EmailTemplateDetailsDto} emailTemplate
   * @param overwrite
   * @returns {Observable<EmailTemplateDetailsDto>}
   */
  update(emailTemplate: EmailTemplateDetailsDto, overwrite: boolean = false): Observable<EmailTemplateDetailsDto> {
    return this.httpPut(
      `${this.bp}/templates/${emailTemplate.id}?overwrite=${overwrite}`,
      emailTemplate,
      optimisticLockingOf(EmailTemplateDetailsMapper));
  }

  checkLevel(id: ID): Observable<EmailTemplateListItemDto> {
    return this.httpGet(`${this.bp}/templates/check-level/${id}`, optimisticLockingOf(EmailTemplateListItemMapper));
  }

  remove(id: ID): Observable<EmailTemplateListItemDto> {
    return this.httpDelete(`${this.bp}/templates/${id}`, void 0, optimisticLockingOf(EmailTemplateListItemMapper));
  }

  getAutocompleteList(): Observable<EmailTemplateAutocompleteListItemDto[]> {
    return this.httpGet(`${this.bp}/templates/autocomplete`, EmailTemplateAutocompleteListMapper);
  }

  // tslint:disable-next-line:max-line-length
  findAllPlaceholdersAsAutocompleteDataSource(filter: TemplatePlaceholderFilterDto): SlicedDataSource<TemplatePlaceholderDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/placeholders`,
      filter,
      TemplatePlaceholderFilterMapper,
      arrayOf(TemplatePlaceholderMapper));
  }

  getTemplate(templateId: ID, contactId: any, jobId: any): Observable<any> {
    return this.httpGet(`${this.bp}/templates/${templateId}/filled?${contactId ? 'contactId=' + contactId : ''}&${jobId ? 'jobId=' + jobId : ''}`, identity());
  }

  getEmailTemplates(): Observable<EmailTemplateDto[]> {
     return this.httpGet(`${this.bp}/templates/list?type=2`, identity());
  }
}
